import React, { useContext, useEffect, useRef, useState } from 'react';

import { useLocation, useNavigate } from 'react-router-dom';
import { AppContext, RefreshStatus } from '../../App';
import {
  fetchRawData,
  getAnalyticsStatus,
  getDetails,
  getOverallFields,
} from '../../Services/PFMClient/DashboardService';
import {
  EVENTCODES,
  pushEventToParent,
  pushEventToPosthog,
} from '../../Utils/eventUtils';
import { isNudgeValid } from '../../Utils/nudgeUtils';
import { ROUTES } from '../../Utils/routes';
import { captureException } from '../../Utils/sentryUtils';
import {
  getTenant,
  setTrendsVisible,
  trendsVisible,
} from '../../Utils/storageUtils';
import { getInitialRenderingCompletedValue } from '../../Utils/tenantConfig';
import { TenantType } from '../../Utils/types';
import AssetsDashboard from '../Dashboard/AssetsDashboard';
import Spends from '../Dashboard/Spends';
import Toast from '../JFS/Common/Toast';
import NativeLoader from '../NativeLoader';
import NudgesScreenDashboard from '../PaisaBazaar/NudgesScreenDashboard';
import Stocks from '../PaisaBazaar/Stocks';
import styles from './index.module.css';

type props = {
  setCurrentRoute?: any;
};

export const enum ASSETSTYPE {
  'BANKS',
  'EQUITIES',
  'MF',
  'STOCKS',
  'FD',
  'ETF',
  'MUTUAL_FUNDS',
  'EQUITIES_ETF',
}

export enum DetailsType {
  ASSET = 'ASSET',
  ACCOUNT_DETAILS = 'ACCOUNT_DETAILS',
  TOP5_FUNDS_TRANSFER = 'TOP5_FUNDS_TRANSFER',
  NUDGES = 'NUDGES',
  MUTUAL_FUNDS = 'MUTUAL_FUNDS',
  EQUITIES_ETF = 'EQUITIES_ETF',
  OVERVIEW_ANALYSIS = 'OVERVIEW_ANALYSIS',
  FD_RD = 'FD_RD',
  EPFO_ANALYSIS = 'EPFO_ANALYSIS',
  CREDIT_ANALYSIS = 'CREDIT_ANALYSIS',
}

const HomePageNew: React.FC<props> = ({ setCurrentRoute }) => {
  const {
    data,
    analyticsStatus,
    setAnalyticsStatus,
    analyticsStatusRef,
    isLoading,
    setIsLoading,
    accountsData,
    setNudges,
    setAccountIds,
    stocksData,
    setStocksData,
    statusData,
    isAccountDetailsFetched,
    setIsAccountDetailsFetched,
    isAnyDataFetched,
    setIsAnyDataFetched,
    analyticsStatusStocks,
    setAnalyticsStatusStocks,
    statusPolling,
    isLoaderBackEventSent,
    setDataRefreshStatus,
    dataRefreshStatusRef,
    termRecurringData,
    setTermRecurringData,
    otherDepositBalance,
    isPollingInProgress,
    isBackEventSent,
    isKeyInshigtsVisible,
    selectedNudge,
    startAdditionalAccountJourney,
    setUpdateStatusPollingAnalyticsStatus,
    getCurrentHeight,
    criticalDataStatus,
    setCriticalDataStatus,
    datafromAnalyticsStatus,
    setForceRender,
    showRefreshToast,
    setShowRefreshToast,
    setRefreshToastText,
    refreshToastText,
    currentRoute,
    initialDeepLinkPath,
    deepLinkHandled,
  }: any = useContext(AppContext);

  const navigate = useNavigate();
  const location = useLocation();

  let previousLength: number = -1;

  const dataFetchLinkRef = useRef<any[]>([]);

  const rawDataFetchId = useRef<string[]>([]);
  const totalAccounts = useRef(0);
  const [isInitialRenderingCompleted, setIsInitialRenderingCompleted] =
    useState(getInitialRenderingCompletedValue);

  // Track polling start time
  const [pollingStartTime] = useState(() => new Date(Date.now()));
  // State to store refreshed FIP map
  const [refreshedFipMap, setRefreshedFipMap] = useState<
    Record<string, string>
  >({});
  const [shownFipIds, setShownFipIds] = useState<string[]>([]);
  const [lastToastFipIds, setLastToastFipIds] = useState<string[]>([]);
  const toastTimerRef = useRef<NodeJS.Timeout | null>(null);

  // New loading states for critical APIs
  const [isCriticalDataLoaded, setIsCriticalDataLoaded] = useState(false);
  const [showRemoveToast, setShowRemoveToast] = useState(false);
  const [shouldThrowError, setShouldThrowError] = useState<Error | null>(null);
  const [removeToastMessage, setRemoveToastMessage] = useState('');

  let totalCount = 0;

  const getDetailsByType = async (type: DetailsType) => {
    return new Promise((resolve) => {
      getDetails({ type })
        .then((res) => {
          if (
            typeof res === 'object' &&
            res !== null &&
            'data' in res &&
            getTenant() === TenantType.AU
          ) {
            setIsAnyDataFetched(true);
          }
          resolve(res);
        })
        .catch((err) => {
          captureException(err);
          resolve('error');
        });
    });
  };

  const throwError = (error?: unknown) => {
    if (analyticsStatusRef.current === 'PENDING') {
      if (error instanceof Error) {
        setShouldThrowError(error);
      } else {
        setShouldThrowError(new Error(String(error)));
      }
    }
  };

  // Function to handle critical APIs (Raw Data + Overview Analysis)
  const getCriticalDataPromise = async () => {
    const overviewAnalysisResult: any = await getDetailsByType(
      DetailsType.OVERVIEW_ANALYSIS
    );

    let overviewAnalysis: any = {};

    if (overviewAnalysisResult !== 'error') {
      overviewAnalysis = overviewAnalysisResult.data;
      setCriticalDataStatus((prev: any) => ({
        ...prev,
        overviewAnalysis: true,
      }));
    }

    data.current = {
      ...data.current,
      OVERVIEW_ANALYSIS: overviewAnalysis,
    };

    // Check if both critical APIs are loaded
    checkCriticalDataCompletion();
  };

  // Function to handle other APIs (can run in background)
  const getOtherDataPromise = async () => {
    const analyticsData = statusData.current?.analyticsData || {};

    if (analyticsData.overallAnalysisId != null) {
      getDetailsByType(DetailsType.ASSET).then((res: any) => {
        if (
          res !== 'error' &&
          res !== 'skipped' &&
          typeof res === 'object' &&
          res !== null &&
          'data' in res
        ) {
          const assets = (res as any).data;
          data.current = {
            ...data.current,
            ASSET: assets,
          };
          setForceRender((n: number) => n + 1);
        }
      });
    }

    if (analyticsData.accountProfilesId != null) {
      getDetailsByType(DetailsType.ACCOUNT_DETAILS).then((res: any) => {
        if (
          res !== 'error' &&
          res !== 'skipped' &&
          typeof res === 'object' &&
          res !== null &&
          'data' in res
        ) {
          const account_details = (res as any).data;
          data.current = {
            ...data.current,
            ACCOUNT_DETAILS: account_details,
          };
          if (
            data.current.ACCOUNT_DETAILS &&
            data.current.ACCOUNT_DETAILS.accountProfileList
          ) {
            setAccountIds(
              data.current.ACCOUNT_DETAILS.accountProfileList.map(
                (accountProfile: any) => accountProfile.account.id
              )
            );
            dataFetchLinkRef.current =
              data.current.ACCOUNT_DETAILS.accountProfileList.map(
                (accountProfile: any) => accountProfile.account.linkedAccRef
              );
            setAccountsData();
          }
          setIsAccountDetailsFetched(true);
          setForceRender((n: number) => n + 1);
        }
      });
    }

    if (analyticsData.nudgesId != null) {
      getDetailsByType(DetailsType.NUDGES).then((res: any) => {
        if (
          res !== 'error' &&
          res !== 'skipped' &&
          typeof res === 'object' &&
          res !== null &&
          'data' in res
        ) {
          const nudges = (res as any).data.nudges;

          const validNudges = nudges.filter((nudge: any) =>
            isNudgeValid(nudge, new Date(), nudges)
          );

          (validNudges as any[]).forEach((nudge: any) => {
            if (typeof nudge.body === 'string') {
              try {
                const parsed = JSON.parse(nudge.body);
                if (typeof parsed === 'object' && parsed !== null) {
                  nudge.body = parsed;
                }
              } catch (e) {}
            }
          });
          data.current = {
            ...data.current,
            NUDGES: validNudges,
          };
          setNudges([...validNudges].sort(() => Math.random() - 0.5));
          setForceRender((n: number) => n + 1);
        }
      });
    }

    if (analyticsData.top5FundTransferId != null) {
      getDetailsByType(DetailsType.TOP5_FUNDS_TRANSFER).then((res: any) => {
        if (
          res !== 'error' &&
          res !== 'skipped' &&
          typeof res === 'object' &&
          res !== null &&
          'data' in res
        ) {
          const top5FundTransfer = (res as any).data;
          data.current = {
            ...data.current,
            TOP5FUNDTRANSFER: top5FundTransfer,
          };
          setForceRender((n: number) => n + 1);
        }
      });
    }

    if (analyticsData.mutualFundAnalysisId != null) {
      getDetailsByType(DetailsType.MUTUAL_FUNDS).then((res: any) => {
        if (
          res !== 'error' &&
          res !== 'skipped' &&
          typeof res === 'object' &&
          res !== null &&
          'data' in res
        ) {
          const mutualFunds = (res as any).data;
          data.current = {
            ...data.current,
            MUTUAL_FUNDS: mutualFunds,
          };
          setForceRender((n: number) => n + 1);
        }
      });
    }

    if (analyticsData.equitiesAndEtfAnalysisId != null) {
      getDetailsByType(DetailsType.EQUITIES_ETF).then((res: any) => {
        if (
          res !== 'error' &&
          res !== 'skipped' &&
          typeof res === 'object' &&
          res !== null &&
          'data' in res
        ) {
          const equitiesETF = (res as any).data;
          data.current = {
            ...data.current,
            EQUITIES_ETF: equitiesETF,
          };
          setForceRender((n: number) => n + 1);
        }
      });
    }
    if (analyticsData.equitiesAndEtfAnalysisId != null) {
      getOverallFields()
        .then((res: any) => {
          if (res !== 'error' && res?.data?.totalPortfolioValue) {
            data.current = {
              ...data.current,
              totalPortfolioValue: res.data.totalPortfolioValue,
            };
            setForceRender((n: number) => n + 1);
          }
        })
        .catch((err) => console.error('Error fetching overall fields:', err));
    }

    if (analyticsData.fdRdAnalysisId != null) {
      getDetailsByType(DetailsType.FD_RD).then((res: any) => {
        if (
          res !== 'error' &&
          res !== 'skipped' &&
          typeof res === 'object' &&
          res !== null &&
          'data' in res
        ) {
          const fdRdData = (res as any).data.data;
          data.current = {
            ...data.current,
            FD_RD: fdRdData,
          };
          setForceRender((n: number) => n + 1);
        }
      });
    }

    if (analyticsData.epfoAnalysisId != null) {
      getDetailsByType(DetailsType.EPFO_ANALYSIS).then((res: any) => {
        if (
          res !== 'error' &&
          res !== 'skipped' &&
          typeof res === 'object' &&
          res !== null &&
          'data' in res
        ) {
          const epfoData = (res as any).data;
          data.current = {
            ...data.current,
            EPFO_ANALYSIS: epfoData,
          };
          setForceRender((n: number) => n + 1);
        }
      });
    }

    if (analyticsData.creditAnalysisId != null) {
      getDetailsByType(DetailsType.CREDIT_ANALYSIS).then((res: any) => {
        if (
          res !== 'error' &&
          res !== 'skipped' &&
          typeof res === 'object' &&
          res !== null &&
          'data' in res
        ) {
          const creditData = (res as any).data;
          data.current = {
            ...data.current,
            CREDIT_ANALYSIS: creditData,
          };
          setForceRender((n: number) => n + 1);
        }
      });
    }
  };

  const getDetailsPromise = async () => {
    await getCriticalDataPromise();
    getOtherDataPromise();
  };

  // Helper to check if there's a deep link for AU Bank
  const hasDeepLink = () => {
    if (getTenant() !== TenantType.AU) return false;
    const deepLinkPath = initialDeepLinkPath?.current || '';
    return (
      deepLinkPath &&
      deepLinkPath !== '/' &&
      deepLinkPath !== '/home' &&
      !deepLinkPath.includes('/dashboard')
    );
  };

  const checkCriticalDataCompletion = () => {
    if (criticalDataStatus.rawData && criticalDataStatus.overviewAnalysis) {
      setIsCriticalDataLoaded(true);
      isLoaderBackEventSent.current = true;
      setIsInitialRenderingCompleted(true);
      // For AU Bank, deep linking handles setIsLoading(false)
      if (!hasDeepLink()) {
        setIsLoading(false);
      }
    }
  };

  // Function to check if user has any existing data (to avoid showing skeleton when linking additional accounts)
  const hasExistingData = () => {
    return (
      data.current?.ACCOUNT_DETAILS?.accountProfileList?.length > 0 ||
      data.current?.OVERVIEW_ANALYSIS?.data?.totalAssetsBalance > 0 ||
      data.current?.RAW_DATA
    );
  };

  const setAccountsData = () => {
    data.current.ACCOUNT_DETAILS.accountProfileList.forEach(
      (accountLocal: any) => {
        const dataTest: any[] = accountLocal.monthlyAnalytics
          ? accountLocal.monthlyAnalytics.map((monthlyAnalytics: any) => {
              return {
                creditAmount: monthlyAnalytics.totalCreditAmount,
                debitAmount: monthlyAnalytics.totalDebitAmount,
                yearMonth: monthlyAnalytics.month + '' + monthlyAnalytics.year,
                investment: monthlyAnalytics.totalInvestmentExpense,
              };
            })
          : [];

        accountsData.current = {
          ...accountsData.current,
          [accountLocal.account.linkedAccRef]: {
            ...accountsData.current[accountLocal.account.linkedAccRef],
            monthlyAnalysis: dataTest,
          },
        };
      }
    );
  };

  const getRawData = async (id?: any[]) => {
    let rawDataId = statusData.current.accountDetails
      .map((acc: any) => {
        if (acc.lastRawDataFetchedAt && acc.status === 'ACTIVE') {
          return acc.lastRawDataId;
        } else {
          return null;
        }
      })
      .filter((a: any) => a != null);

    const findDifference = rawDataId.filter(
      (x: any) => !rawDataFetchId.current.includes(x)
    );

    if ((!id || id.length === 0) && findDifference.length === 0) {
      setCriticalDataStatus((prev: any) => ({ ...prev, rawData: true }));
      checkCriticalDataCompletion();
      return;
    }

    if ((id && id.length > 0) || findDifference.length > 0) {
      await fetchRawData({
        dataRequestDetailsIds: id && id.length > 0 ? id : findDifference,
      }).then((res) => {
        totalAccounts.current = statusData.current.accountDetails.length;
        rawDataFetchId.current = [...rawDataFetchId.current, ...findDifference];

        let formattedRawData: any = {};
        res.data.forEach((d: any) => {
          if (d.rawData && d.rawData.Summary && d.rawData.type === 'deposit') {
            formattedRawData[d.rawData.linkedAccRef] =
              parseInt(d.rawData.Summary.currentBalance) ?? 0;
          } else if (
            d.rawData &&
            d.rawData.Summary &&
            (d.rawData.type === 'term_deposit' ||
              d.rawData.type === 'recurring_deposit')
          ) {
            formattedRawData[d.dataRequestDetailsId] =
              parseInt(d.rawData.Summary.currentValue || 0) ?? 0;
            otherDepositBalance.current +=
              parseInt(d.rawData.Summary.currentValue || 0) ?? 0;
          }
        });

        data.current = {
          ...data.current,
          ASSET: {
            ...data.current.ASSET,
            totalAccounts: statusData.current.accountDetails.filter(
              (acc: any) =>
                acc.fiType === 'DEPOSIT' &&
                acc.lastRequestFIStatus === 'DELIVERED'
            ).length,
          },
          RAW_DATA: { ...data.current.RAW_DATA, ...formattedRawData },
          USERNAME: res.data[0]?.rawData?.Profile?.Holders?.Holder[0]?.name
            ? res.data[0].rawData.Profile.Holders.Holder[0]?.name
            : 'User',
        };

        pushEventToParent({
          eventCode: EVENTCODES.RAW_DATA_FETCHED,
        });

        setCriticalDataStatus((prev: any) => ({ ...prev, rawData: true }));
        checkCriticalDataCompletion();

        const stocksFilter = res.data.filter(
          (rawDataObject: any) =>
            rawDataObject?.rawData?.type?.toLowerCase() === 'equities' ||
            rawDataObject?.rawData?.type?.toLowerCase() === 'etf'
        );

        const termRecurringFilter = res.data.filter(
          (rawDataObject: any) =>
            rawDataObject?.rawData?.type === 'recurring_deposit' ||
            rawDataObject?.rawData?.type === 'term_deposit'
        );

        if (termRecurringFilter.length > 0) {
          addItemsTermRecurringDeposit(termRecurringFilter, id ? id : []);
        }

        if (stocksFilter.length > 0) {
          setAnalyticsStatusStocks('DELIVERED');
          addItems(stocksFilter, id ? id : []);
        }
      });
    }
  };

  const addItemsTermRecurringDeposit = (newItemsArray: any, id: any[]) => {
    const uniqueItems = newItemsArray.filter(
      (newItem: any) =>
        !termRecurringData.some(
          (existingItem: any) =>
            existingItem.dataRequestDetailsId === newItem.dataRequestDetailsId
        )
    );

    let termRecurringDataLocal =
      id && id.length > 0 ? [] : [...termRecurringData];
    termRecurringDataLocal.push(...uniqueItems);

    setTermRecurringData(termRecurringDataLocal);
  };

  const addItems = (newItemsArray: any, id: any[]) => {
    const uniqueItems = newItemsArray.filter(
      (newItem: any) =>
        !stocksData.some(
          (existingItem: any) =>
            existingItem.dataRequestDetailsId === newItem.dataRequestDetailsId
        )
    );

    let stocksDataLocal = id && id.length > 0 ? [] : [...stocksData];
    stocksDataLocal.push(...uniqueItems);

    setStocksData(stocksDataLocal);
  };

  const fetchStocksRawData = async () => {
    const filterStocksData = statusData.current.accountDetails
      .map((acc: any) => {
        if (
          acc.lastRawDataFetchedAt &&
          acc.status === 'ACTIVE' &&
          acc.fiType === 'EQUITIES'
        ) {
          return acc.lastRawDataId;
        } else {
          return null;
        }
      })
      .filter((a: any) => a != null);

    if (filterStocksData && filterStocksData.length > 0) {
      await getRawData(filterStocksData);
    }
  };

  const checkDataRefreshStatus = async () => {
    const statusDataLocal = statusData.current;

    if (statusDataLocal.refreshAvailable) {
      setDataRefreshStatus(RefreshStatus.REFRESH_AVAILABLE);
    } else if (
      statusDataLocal.status === 'ASYNC_COMPLETED' &&
      statusDataLocal.lastRefreshStatus === 'COMPLETED' &&
      dataRefreshStatusRef.current === RefreshStatus.IN_PROGRESS
    ) {
      let totalDepositAccounts = 0;
      let totalStocksAccounts = 0;
      let totalCompletedDataFetch = 0;
      let totalFailedDataFetch = 0;
      let totalFdRdAccounts = 0;

      statusData.current.accountDetails.forEach((r: any) => {
        if (r.status === 'ACTIVE' && r.fiType === 'DEPOSIT') {
          totalDepositAccounts += 1;

          if (
            r.lastRequestAnalyticsStatus === 'ASYNC_COMPLETED' &&
            r.lastRequestFIStatus === 'DELIVERED'
          ) {
            totalCompletedDataFetch += 1;
          } else if (r.status === 'ACTIVE' && r.fiType === 'DEPOSIT') {
            totalFailedDataFetch += 1;
          }
        }

        if (
          r.status === 'ACTIVE' &&
          (r.fiType === 'TERM_DEPOSIT' ||
            r.fiType === 'RECURRING_DEPOSIT' ||
            r.fiType === 'TERM-DEPOSIT')
        ) {
          totalFdRdAccounts += 1;

          if (
            r.lastRequestFIStatus === 'DATA_NOT_RETURNED_IN_TIME' ||
            r.lastRequestFIStatus === 'TIMEOUT' ||
            r.lastRequestFIStatus === 'DENIED'
          ) {
            totalFailedDataFetch += 1;
          } else {
            totalCompletedDataFetch += 1;
          }
        }

        if (
          r.status === 'ACTIVE' &&
          (r.fiType === 'EQUITIES' || r.fiType === 'ETF')
        ) {
          totalStocksAccounts += 1;

          if (
            r.lastRequestFIStatus === 'DATA_NOT_RETURNED_IN_TIME' ||
            r.lastRequestFIStatus === 'TIMEOUT' ||
            r.lastRequestFIStatus === 'DENIED'
          ) {
            totalFailedDataFetch += 1;
          } else {
            totalCompletedDataFetch += 1;
          }
        }
      });

      if (
        totalDepositAccounts + totalStocksAccounts + totalFdRdAccounts ===
        totalCompletedDataFetch
      ) {
        pushEventToParent({
          eventCode: EVENTCODES.REFRESH_COMPLETED,
        });
        setDataRefreshStatus(RefreshStatus.COMPLETED);
      } else if (
        totalDepositAccounts + totalStocksAccounts + totalFdRdAccounts ===
        totalFailedDataFetch
      ) {
        setDataRefreshStatus(RefreshStatus.FAILURE);
      } else {
        pushEventToParent({
          eventCode: EVENTCODES.REFRESH_COMPLETED_PARTIAL,
        });
        setDataRefreshStatus(RefreshStatus.PARTIAL_COMPLETED);
      }

      await fetchStocksRawData();
    }
  };

  // Helper to get sum and count of closingBalance by fiType
  const getFiTypeStats = () => {
    const accountDetails =
      statusData.current.accountDetails.filter(
        (acc: any) => acc.status !== 'INACTIVE'
      ) || [];

    const fistats = accountDetails.reduce(
      (acc: Record<string, { sum: number; count: number }>, curr: any) => {
        const { fiType, closingBalance } = curr;
        if (!fiType) return acc;
        const balance = closingBalance ? Number(closingBalance) : 0;
        if (!acc[fiType]) {
          acc[fiType] = { sum: 0, count: 0 };
        }
        acc[fiType].sum += balance;
        acc[fiType].count += 1;
        return acc;
      },
      {}
    );

    // Check for EPFO and CREDIT_BUREAU from analyticsData when not in accountDetails
    const analyticsData = statusData.current?.analyticsData || {};
    const hasEpfo =
      fistats?.EPFO?.count > 0 || analyticsData.epfoAnalysisId != null;
    const hasCreditBureau =
      fistats?.CREDIT_BUREAU?.count > 0 || analyticsData.creditAnalysisId != null;

    datafromAnalyticsStatus.current = {
      ...datafromAnalyticsStatus.current,
      savings: fistats?.DEPOSIT?.sum || 0,
      mutualFunds: fistats?.MUTUAL_FUNDS?.sum || 0,
      equities: fistats?.EQUITIES?.sum || 0,
      etf: fistats?.ETF?.sum || 0,
      fdRd:
        (fistats?.TERM_DEPOSIT?.sum || 0) +
        (fistats?.['TERM-DEPOSIT']?.sum || 0) +
        (fistats?.RECURRING_DEPOSIT?.sum || 0),
      epfo: hasEpfo,
      creditBureau: hasCreditBureau,
    };
    const { savings, mutualFunds, equities, etf, fdRd, epfo, creditBureau } =
      datafromAnalyticsStatus.current;
    const prev = datafromAnalyticsStatus.current.prev || {};
    const isDifferent =
      savings !== prev.savings ||
      mutualFunds !== prev.mutualFunds ||
      equities !== prev.equities ||
      etf !== prev.etf ||
      fdRd !== prev.fdRd ||
      epfo !== prev.epfo ||
      creditBureau !== prev.creditBureau;
    if (savings > 0 || mutualFunds > 0 || equities > 0 || etf > 0 || fdRd > 0 || epfo || creditBureau) {
      setIsInitialRenderingCompleted(true);
      // For AU Bank, deep linking handles setIsLoading(false)
      if (!hasDeepLink()) {
        setIsLoading(false);
      }
      if (isDifferent) {
        setForceRender((n: number) => n + 1);
        datafromAnalyticsStatus.current.prev = {
          savings,
          mutualFunds,
          equities,
          etf,
          fdRd,
          epfo,
          creditBureau,
        };
      }
    }
  };

  // Ref to track previous fiType status mapping
  const prevAccountStatesRef = useRef<
    Record<string, { status: string; lastRequestAnalyticsStatus: string }>
  >({});

  const makeRequestWithRetry = () => {
    if (totalCount < 500) {
      // push events only for 10 times
      if (totalCount < 10) {
        pushEventToPosthog({ eventCode: EVENTCODES.STATUS_POLLING });
      }

      getAnalyticsStatus()
        .then((res) => res.data)
        .then(async (res: any) => {
          statusData.current = res;
          // Track refreshed FIPs

          const checkAllAccountsInactive = () => {
            const accountDetails = res.accountDetails || [];

            if (!res.accountDetails || accountDetails.length === 0) return true;

            const bankAccounts = accountDetails.filter(
              (acc: any) => acc.fiType === 'DEPOSIT'
            );
            const stockAccounts = accountDetails.filter(
              (acc: any) => acc.fiType === 'EQUITIES' || acc.fiType === 'ETF'
            );
            const mutualFundAccounts = accountDetails.filter(
              (acc: any) => acc.fiType === 'MUTUAL_FUNDS'
            );

            const fdRdAccounts = accountDetails.filter(
              (acc: any) =>
                acc.fiType === 'TERM_DEPOSIT' ||
                acc.fiType === 'TERM-DEPOSIT' ||
                acc.fiType === 'RECURRING_DEPOSIT'
            );

            const allBankAccountsInactive =
              bankAccounts.length === 0 ||
              bankAccounts.every((acc: any) => acc.status === 'INACTIVE');
            const allStockAccountsInactive =
              stockAccounts.length === 0 ||
              stockAccounts.every((acc: any) => acc.status === 'INACTIVE');
            const allMutualFundAccountsInactive =
              mutualFundAccounts.length === 0 ||
              mutualFundAccounts.every((acc: any) => acc.status === 'INACTIVE');
            const allFdRdAccountInactive =
              fdRdAccounts.length === 0 ||
              fdRdAccounts.every((acc: any) => acc.status === 'INACTIVE');

            const hasAnyAccounts =
              bankAccounts.length > 0 ||
              stockAccounts.length > 0 ||
              mutualFundAccounts.length > 0 ||
              fdRdAccounts.length > 0;

            if (
              hasAnyAccounts &&
              allBankAccountsInactive &&
              allStockAccountsInactive &&
              allMutualFundAccountsInactive &&
              allFdRdAccountInactive
            ) {
              return true;
            }

            return false;
          };

          if (!checkAllAccountsInactive()) {
            let wasGetdetailsCalled = false;
            // const refreshedMap: Record<string, string> = { ...refreshedFipMap };
            // (res.accountDetails || [])
            //   .filter((acc: any) => acc.status !== 'INACTIVE')
            //   .forEach((acc: any) => {
            //     // Convert lastAnalyzedAt to IST ms for comparison

            //     // Log both times in readable IST format

            //     if (
            //       acc.lastAnalyzedAt &&
            //       acc.status === 'ACTIVE' &&
            //       acc.fipId &&
            //       acc.fipName &&
            //       new Date(acc.lastAnalyzedAt) > pollingStartTime
            //     ) {
            //       refreshedMap[acc.fipId] = acc.fipName;
            //     }
            //   });
            // // Only update state if the map has changed
            // const prevKeys = Object.keys(refreshedFipMap);
            // const newKeys = Object.keys(refreshedMap);
            // const isMapChanged =
            //   prevKeys.length !== newKeys.length ||
            //   prevKeys.some((k) => refreshedFipMap[k] !== refreshedMap[k]);

            // if (isMapChanged) {
            //   setRefreshedFipMap(refreshedMap);
            // }

            getFiTypeStats();

            setUpdateStatusPollingAnalyticsStatus((old: number) => old + 1);
            checkDataRefreshStatus();

            if (!trendsVisible() && res.trendsVisible) {
              setTrendsVisible(true);
            }

            if (getTenant() === TenantType.JFS && res.accountDetails) {
              const activeAccounts = res.accountDetails.filter(
                (account: any) => account.status === 'ACTIVE'
              );
              if (
                activeAccounts.length > 0 &&
                activeAccounts.every(
                  (account: any) => account.lastAnalyzedAt === null
                ) &&
                activeAccounts.every(
                  (account: any) =>
                    account.lastRequestFIStatus === 'DENIED' ||
                    account.lastRequestFIStatus === 'TIMEOUT' ||
                    account.lastRequestFIStatus === 'DATA_NOT_RETURNED_IN_TIME'
                )
              ) {
                setCurrentRoute(ROUTES.JFS_DATA_FAILED);
                navigate(ROUTES.JFS_DATA_FAILED);
                return;
              }
            }

            const checkStatusTermRecurring: any[] = res.accountDetails.filter(
              (d: any) =>
                (d.fiType === 'TERM_DEPOSIT' ||
                  d.fiType === 'TERM-DEPOSIT' ||
                  d.fiType === 'RECURRING_DEPOSIT') &&
                d.status === 'ACTIVE' &&
                d.lastRequestFIStatus === 'DELIVERED'
            );

            if (
              checkStatusTermRecurring.length === res.accountDetails.length &&
              getTenant() === TenantType.YESBANK
            ) {
              setIsAccountDetailsFetched(true);
            }

            const checkStatus: any[] = res.accountDetails.filter(
              (d: any) =>
                (d.lastRequestAnalyticsStatus !== 'ASYNC_COMPLETED' &&
                  d.fiType === 'DEPOSIT' &&
                  d.status === 'ACTIVE' &&
                  (d.lastRequestFIStatus === 'DELIVERED' ||
                    d.lastRequestFIStatus === 'PENDING' ||
                    d.lastRequestFIStatus === null)) ||
                d.status === 'REVOKE_INITIATED'
            );

            const checkStatusStocks: any[] = res.accountDetails.filter(
              (d: any) =>
                (d.fiType === 'EQUITIES' || d.fiType === 'ETF') &&
                d.status === 'ACTIVE'
            );

            const isRefreshPending = res.lastRefreshStatus === 'PENDING';

            if (
              res.status &&
              res.status !== null &&
              (res.status === 'ASYNC_PROCESSING' ||
                res.status === 'ASYNC_COMPLETED' ||
                res.status === 'REFRESH_PROCESSING' ||
                res.status === 'REFRESH_INITIATE_FAILED')
            ) {
              if (
                analyticsStatusRef.current !== 'ASYNC_PROCESSING' &&
                analyticsStatusRef.current !== 'ASYNC_COMPLETED' &&
                analyticsStatusRef.current !== 'REFRESH_PROCESSING' &&
                analyticsStatusRef.current !== 'REFRESH_INITIATE_FAILED'
              ) {
                if (analyticsStatusRef.current !== 'RAW_DATA_FETCHED') {
                  getRawData();
                }
                wasGetdetailsCalled = true;

                await getDetailsPromise();
                setAnalyticsStatus(res.status);
              }
            } else if (res.rawDataFetched) {
              if (
                analyticsStatusRef.current !== 'RAW_DATA_FETCHED' ||
                totalAccounts.current !== rawDataFetchId.current.length
              ) {
                await getRawData();
                setAnalyticsStatus('RAW_DATA_FETCHED');
                isLoaderBackEventSent.current = true;
              }
            } else if (res.userConsentActive) {
              setAnalyticsStatus('CONSENT_APPROVED');
            }

            if (
              (checkStatus && checkStatus.length > 0) ||
              isRefreshPending ||
              res.status === null
            ) {
              previousLength = checkStatus.length;
            } else if (
              previousLength !== -1 &&
              previousLength !== checkStatus.length
            ) {
              previousLength = checkStatus.length;
              wasGetdetailsCalled = true;

              await getDetailsPromise();
            }

            if (checkStatus && checkStatus.length > 0) {
              const filterAcc = checkStatus.map((acc) => {
                if (
                  (acc.lastRequestAnalyticsStatus === 'ASYNC_COMPLETED' ||
                    acc.lastRequestAnalyticsStatus === 'ASYNC_PROCESSING' ||
                    acc.lastRequestAnalyticsStatus === 'REFRESH_PROCESSING' ||
                    acc.lastRequestAnalyticsStatus ===
                      'REFRESH_INITIATE_FAILED') &&
                  !dataFetchLinkRef.current.includes(acc.linkRefNumber)
                ) {
                  return acc.linkRefNumber;
                }
              });

              if (filterAcc.filter((e) => e !== undefined).length > 0) {
                wasGetdetailsCalled = true;
                getDetailsPromise();
                dataFetchLinkRef.current =
                  dataFetchLinkRef.current.concat(filterAcc);
              }
            }

            if (checkStatusStocks && checkStatusStocks.length > 0) {
              const filterStatus: any[] = checkStatusStocks.filter(
                (d: any) => d.lastRequestFIStatus !== 'DELIVERED'
              );

              if (filterStatus.length === 0) {
                await getRawData();
              }
            } else if (analyticsStatusStocks === 'PENDING') {
              setAnalyticsStatusStocks('NO_ACCOUNTS');
            }
            if (!wasGetdetailsCalled) {
              // --- Begin: Detect account add/change for getDetailsPromise ---
              const currentAccounts = res.accountDetails || [];
              const currentAccountMap: Record<
                string,
                { status: string; lastRequestAnalyticsStatus: string }
              > = {};
              let shouldCallGetDetails = false;
              currentAccounts.forEach((acc: any) => {
                const key = `${acc.maskedAccountNumber}_${acc.referenceId}`;
                currentAccountMap[key] = {
                  status: acc.status,
                  lastRequestAnalyticsStatus: acc.lastRequestAnalyticsStatus,
                };
                const prev = prevAccountStatesRef.current[key];
                if (!prev) {
                  // New account added
                  shouldCallGetDetails = true;
                } else if (
                  prev.status !== acc.status ||
                  prev.lastRequestAnalyticsStatus !==
                    acc.lastRequestAnalyticsStatus
                ) {
                  // Status or lastRequestAnalyticsStatus changed
                  shouldCallGetDetails = true;
                }
              });
              if (shouldCallGetDetails) {
                getDetailsPromise();
                prevAccountStatesRef.current = currentAccountMap;
              } else if (getTenant() === TenantType.AU && totalCount === 0) {
                // First response only: No new data to fetch, set flag and redirect to dashboard for deep linking
                setIsAnyDataFetched(true);
                const pathname = initialDeepLinkPath?.current || '';
                if (!pathname.includes('/creditScore')) {
                  setCurrentRoute?.(ROUTES.DASHBOARD);
                  setIsLoading(false);
                }
              }
            }
            setTimeout(() => {
              makeRequestWithRetry();
              totalCount += 1;
            }, 5000);
          } else {
            isPollingInProgress.current = false;
            if (getTenant() === TenantType.JFS) {
              setCurrentRoute(ROUTES.JFS_WELCOME);
              navigate(ROUTES.JFS_WELCOME);
            } else if (getTenant() === TenantType.AU && totalCount === 0) {
              // First response only: All accounts are inactive, set flag and redirect to dashboard
              setIsAnyDataFetched(true);
              const pathname = initialDeepLinkPath?.current || '';
              if (!pathname.includes('/creditScore')) {
                setCurrentRoute?.(ROUTES.DASHBOARD);
                setIsLoading(false);
              }
            }
          }
        })
        .catch((err) => {
          captureException(err);
          throwError(err);
        });
    } else {
      throwError();
      isPollingInProgress.current = false;
    }
  };

  useEffect(() => {
    if (
      (analyticsStatus === 'PENDING' || analyticsStatusStocks === 'PENDING') &&
      !isPollingInProgress.current
    ) {
      isPollingInProgress.current = true;
      makeRequestWithRetry();
    } else if (
      (analyticsStatus === 'PENDING' || analyticsStatusStocks === 'PENDING') &&
      isPollingInProgress.current
    ) {
      totalCount = 0;
    }
  }, [statusPolling]);

  useEffect(() => {
    checkCriticalDataCompletion();
  }, [criticalDataStatus]);

  // Check for removed banks toast message
  useEffect(() => {
    const removedBanks = sessionStorage.getItem('removedBanks');
    if (removedBanks) {
      try {
        const bankNames = JSON.parse(removedBanks);
        const bankText =
          bankNames.length > 1
            ? `${bankNames.join(', ')} accounts`
            : `${bankNames[0]} accounts`;
        setRemoveToastMessage(
          `${bankText} removed. Balance will be updated in a few moments.`
        );
        setShowRemoveToast(true);
        sessionStorage.removeItem('removedBanks');
      } catch (error) {
        console.error('Error parsing removed banks:', error);
        captureException(error);
      }
    }
  }, [location]);

  useEffect(() => {
    if (!refreshToastText) {
      // Get all unique active FIPs from analytics API
      const analyticsFips = (statusData.current?.accountDetails || [])
        .filter((f: any) => f.status === 'ACTIVE')
        .map((f: any) => f.fipName);
      const uniqueAnalyticsFipNames = Array.from(new Set(analyticsFips));
      const totalUniqueActiveFipCount = uniqueAnalyticsFipNames.length;

      // Only show FIPs that haven't been shown before
      const fipIdsToShow = Object.keys(refreshedFipMap).filter(
        (fipId) => !shownFipIds.includes(fipId)
      );
      const refreshedFipNames = fipIdsToShow.map(
        (fipId) => refreshedFipMap[fipId]
      );
      const refreshedCount = refreshedFipNames.length;

      let toastText = '';
      if (refreshedCount === totalUniqueActiveFipCount && refreshedCount > 0) {
        toastText =
          'Your data is successfully refreshed, you should see updated values now.';
      } else if (refreshedCount > 3) {
        toastText =
          'Some of your data has been refreshed. You should see updated values now.';
      } else if (refreshedCount > 0) {
        if (refreshedCount === 1) {
          toastText = `Your ${refreshedFipNames[0]} data has been refreshed. You should see updated values now.`;
        } else if (refreshedCount === 2) {
          toastText = `Your ${refreshedFipNames[0]} and ${refreshedFipNames[1]} data has been refreshed. You should see updated values now.`;
        } else {
          toastText =
            'Some of your data has been refreshed. You should see updated values now.';
        }
      }

      if (toastText) {
        setLastToastFipIds(fipIdsToShow);
        setRefreshToastText(toastText);
        setShowRefreshToast(true);
      }
    }
    // eslint-disable-next-line
  }, [refreshedFipMap]);

  useEffect(() => {
    if (
      typeof showRefreshToast !== 'undefined' &&
      showRefreshToast &&
      refreshToastText
    ) {
      // Clear any existing timer
      if (toastTimerRef.current) {
        clearTimeout(toastTimerRef.current);
      }
      toastTimerRef.current = setTimeout(() => {
        setShowRefreshToast(false);
        // Mark FIPs as shown only after the toast is hidden
        if (lastToastFipIds.length > 0) {
          setShownFipIds((prev) => [
            ...prev,
            ...lastToastFipIds.filter((fipId) => !prev.includes(fipId)),
          ]);
        }
      }, 3000);
    }
    return () => {
      if (toastTimerRef.current) {
        clearTimeout(toastTimerRef.current);
      }
    };
  }, [showRefreshToast, refreshToastText, lastToastFipIds]);

  const [spendView, setSpendView] = useState(false);
  const [stocksView, setStocksView] = useState(false);

  const onClick = (assestType: ASSETSTYPE) => {
    if (assestType === ASSETSTYPE.BANKS) {
      setSpendView(true);
    }
    if (assestType === ASSETSTYPE.EQUITIES_ETF) {
      setStocksView(true);
    }
  };

  // Deep linking for AU Bank - route based on URL pathname
  // Helper to check if FI type exists in account details
  const hasFiType = (fiTypes: string[]) => {
    const accountDetails = statusData?.current?.accountDetails || [];
    return accountDetails.some(
      (acc: any) => acc.status === 'ACTIVE' && fiTypes.includes(acc.fiType)
    );
  };

  useEffect(() => {
    if (getTenant() !== TenantType.AU) return;
    if (!isAnyDataFetched) return;
    if (deepLinkHandled.current) return;

    deepLinkHandled.current = true;

    const pathname = initialDeepLinkPath?.current || '';
    let routeChanged = false;

    if (pathname.includes('/spends') && hasFiType(['DEPOSIT'])) {
      setCurrentRoute?.(ROUTES.AU_SPENDS);
      navigate(ROUTES.AU_SPENDS);
      routeChanged = true;
    } else if (pathname.includes('/stocks') && hasFiType(['EQUITIES', 'ETF'])) {
      setCurrentRoute?.(ROUTES.AU_STOCKS);
      navigate(ROUTES.AU_STOCKS);
      routeChanged = true;
    } else if (
      pathname.includes('/mutualFunds') &&
      hasFiType(['MUTUAL_FUNDS'])
    ) {
      setCurrentRoute?.(ROUTES.AU_MUTUAL_FUNDS);
      navigate(ROUTES.AU_MUTUAL_FUNDS);
      routeChanged = true;
    } else if (
      pathname.includes('/deposit') &&
      hasFiType(['TERM_DEPOSIT', 'TERM-DEPOSIT', 'RECURRING_DEPOSIT'])
    ) {
      setCurrentRoute?.(ROUTES.AU_FDRD);
      navigate(ROUTES.AU_FDRD);
      routeChanged = true;
    } else if (pathname.includes('/epfo')) {
      // EPF doesn't require FI check (mock data)
      setCurrentRoute?.(ROUTES.AU_EPF);
      navigate(ROUTES.AU_EPF);
      routeChanged = true;
    } else if (pathname.includes('/nudges')) {
      setCurrentRoute?.(ROUTES.AU_NUDGES);
      navigate(ROUTES.AU_NUDGES);
      routeChanged = true;
    }

    // If no deep link matched, ensure we stay on dashboard and set loading to false
    if (!routeChanged) {
      setCurrentRoute?.(ROUTES.DASHBOARD);
    }

    // Set loading to false after deep link processing
    setIsLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAnyDataFetched]);

  if (shouldThrowError) {
    throw shouldThrowError;
  }

  return !isInitialRenderingCompleted ? (
    <NativeLoader loaderHeight={getCurrentHeight()} />
  ) : isLoading && getTenant() !== TenantType.YESBANK && !hasExistingData() ? (
    <></>
  ) : (
    <>
      {isKeyInshigtsVisible || selectedNudge ? (
        <NudgesScreenDashboard />
      ) : (
        <></>
      )}
      {!isKeyInshigtsVisible && !selectedNudge ? (
        <div className={styles.dashboard_main_div}>
          {isInitialRenderingCompleted ? (
            spendView ? (
              <Spends
                onBack={() => {
                  setSpendView(false);
                  isBackEventSent.current = false;
                }}
                defaultTab={'1'}
              />
            ) : stocksView ? (
              <Stocks />
            ) : (
              <AssetsDashboard onClick={onClick} />
            )
          ) : (
            <></>
          )}
        </div>
      ) : (
        <></>
      )}

      {showRemoveToast && (
        <Toast
          message={removeToastMessage}
          duration={5000}
          onClose={() => setShowRemoveToast(false)}
        />
      )}
    </>
  );
};

export default HomePageNew;
